import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import QuantityCounter from '../../../components/ui/QuantityCounter';
import { formatCurrency } from '../../../utils/formatters';
import { AppIcon } from '../../../components/icons/AppIcon';

interface ChangeLicensesModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentQuantity: number;
    licensesInUse: number;
    pricePerLicense: number;
    planName: string;
    currentPeriodEnd: string | null;
    onConfirm: (newQuantity: number) => void;
    isLoading?: boolean;
    minQuantity?: number;
}

const ChangeLicensesModal: React.FC<ChangeLicensesModalProps> = ({
    isOpen,
    onClose,
    currentQuantity,
    licensesInUse,
    pricePerLicense,
    currentPeriodEnd,
    onConfirm,
    isLoading = false,
    minQuantity = 1,
}) => {
    const [quantity, setQuantity] = useState(currentQuantity);

    useEffect(() => {
        if (isOpen) {
            setQuantity(currentQuantity);
        }
    }, [isOpen, currentQuantity]);

    const licensesAvailable = useMemo(() => Math.max(0, quantity - licensesInUse), [quantity, licensesInUse]);
    const isReducing = quantity < currentQuantity;
    const hasChange = quantity !== currentQuantity;
    const newTotalPrice = quantity * pricePerLicense;
    const effectiveMin = Math.max(minQuantity, licensesInUse);

    const formattedPeriodEnd = useMemo(() => {
        if (!currentPeriodEnd) return null;
        return new Date(currentPeriodEnd).toLocaleDateString('pt-BR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    }, [currentPeriodEnd]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <Modal.Header onClose={onClose}>
                Alterar o número de licenças de Negócios
            </Modal.Header>
            <Modal.Body>
                <p className="mb-6 text-sm text-secondary">
                    Escolha o número que você precisará por mês.
                </p>

                <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <QuantityCounter
                            value={quantity}
                            onChange={setQuantity}
                            min={effectiveMin}
                            max={999}
                        />
                        <p className="mt-3 text-sm text-muted">
                            {licensesInUse} licenças em uso, {licensesAvailable} disponível
                        </p>
                    </div>

                    <div className="text-left sm:text-right">
                        <p className="metric-value text-3xl">
                            {formatCurrency(newTotalPrice)}
                        </p>
                        <p className="mt-1 text-sm text-muted">
                            por empresa/mês ({formatCurrency(pricePerLicense)} por licença)
                        </p>
                    </div>
                </div>

                {isReducing && formattedPeriodEnd && (
                    <div className="mt-6 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                        <AppIcon name="alert-triangle" className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                        <p className="text-sm text-amber-800">
                            Esta alteração entra em vigor em <strong>{formattedPeriodEnd}</strong> (fim do ciclo de cobrança atual).
                            Apenas licenças vagas podem ser excluídas.
                        </p>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                    Cancelar
                </Button>
                <Button
                    variant="success"
                    onClick={() => onConfirm(quantity)}
                    disabled={!hasChange || isLoading}
                >
                    {isLoading ? 'Processando...' : 'Ir para o pagamento'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ChangeLicensesModal;
